import { DecodeContinuouslyCallback } from "@zxing/browser/esm/common/DecodeContinuouslyCallback";
import { BrowserQRCodeReader, IScannerControls } from "@zxing/browser";
import { useEffect, useRef, useState } from "react";
import { useIonViewDidEnter, useIonViewDidLeave } from "@ionic/react";
import { Log } from "@greeter/log";
import { classNames } from "@greeter/util";
import { Card } from "@greeter/matter-v2";
import { IconQrcode } from "@tabler/icons-react";
import css from "./Cam.module.scss";
import { Signal } from "@greeter/event";
import { Capacitor } from "@capacitor/core";
import { Camera } from "@capacitor/camera";

type OnScanFn = DecodeContinuouslyCallback;

type CameraState = "starting" | "started";
type StartCameraResult = {
  hasCameraAccess: boolean;
  stream?: MediaStream | null;
  controls?: IScannerControls | null;
};

type CameraManagerEvents =
  | {
      type: "starting";
    }
  | { type: "started" }
  | { type: "cleaningup" }
  | { type: "cleanedup" };

type OnFn = () => void;

class CameraManager {
  state: CameraState = "started";

  signals: Map<CameraManagerEvents["type"], Signal> = new Map();

  controls?: IScannerControls;
  stream?: MediaStream | null;
  hasCameraAccess = false;
  reader = new BrowserQRCodeReader(undefined, {
    delayBetweenScanAttempts: 1_000,
  });

  on(eventType: CameraManagerEvents["type"], fn: OnFn) {
    let signal = this.signals.get(eventType);
    if (!signal) {
      signal = new Signal();
      this.signals.set(eventType, signal);
    }
    signal.subscribe(fn);

    return this;
  }

  off(eventType: CameraManagerEvents["type"], fn: OnFn) {
    this.signals.get(eventType)?.unsubscribe(fn);
  }

  private emit(eventType: CameraManagerEvents["type"]) {
    this.signals.get(eventType)?.emit();
  }

  /**
   * NOTE: Defined like this, as it maintains the `this` reference to this class when passing this as reference. Meaning, we don't have to .bind this class to the function
   */
  cleanup = async () => {
    this.emit("cleaningup");
    if (this.state === "starting") {
      await new Promise<void>((resolve, reject) => {
        const onStarted = () => {
          this.off("started", onStarted);
          resolve();
        };
        this.on("started", onStarted);
        setTimeout(reject, 10_000);
      });
    }
    this.stream?.getTracks().forEach((track) => track.stop());
    this.controls?.stop();
    this.emit("cleanedup");
  };

  async startCamera(
    videoElement: HTMLVideoElement | null = null,
    opts: { scan: OnScanFn }
  ): Promise<StartCameraResult> {
    if (this.state === "starting") {
      this.cleanup();
    }

    this.state = "starting";
    this.emit("starting");

    this.hasCameraAccess = false;

    try {
      if (Capacitor.isNativePlatform()) {
        const s = await Camera.requestPermissions({
          permissions: ["camera"],
        });
        if (s.camera === "denied") {
          this.hasCameraAccess = false;
          return {
            hasCameraAccess: this.hasCameraAccess,
            stream: this.stream,
            controls: this.controls,
          };
        }
      }

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      if (!videoElement)
        return {
          hasCameraAccess: this.hasCameraAccess,
          stream: this.stream,
          controls: this.controls,
        };

      videoElement.srcObject = this.stream;
      if (opts?.scan)
        this.controls = await this.reader.decodeFromStream(
          this.stream,
          videoElement,
          opts?.scan
        );
      this.hasCameraAccess = true;
    } catch (error) {
      this.hasCameraAccess = false;
      Log.error("Error accessing camera", { error });
    }

    this.state = "started";
    this.emit("started");

    return this.getState();
  }

  getState() {
    return {
      stream: this.stream,
      controls: this.controls,
      hasCameraAccess: this.hasCameraAccess,
    };
  }
}

export function Cam(props: {
  onDecoded: (s: string) => void;
  scanning: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [hasCameraAccess, setHasCameraAccess] = useState(false);

  const cameraManagerRef = useRef(new CameraManager());

  useEffect(() => {
    const tagged = (s: string) => `[CameraManager] ${s}`;
    cameraManagerRef.current.on("cleaningup", () =>
      Log.debug(tagged("cleaningup"))
    );
    cameraManagerRef.current.on("cleanedup", () =>
      Log.debug(tagged("cleanedup"))
    );
    cameraManagerRef.current.on("starting", () =>
      Log.debug(tagged("starting"))
    );
    cameraManagerRef.current.on("started", () => Log.debug(tagged("started")));
  }, []);

  async function setupCamera() {
    return await cameraManagerRef.current.startCamera(videoRef.current, {
      scan(r, e, _c) {
        if (!props.scanning) return;
        if (e) {
          Log.debug("Could not identify QR code with error", { e });
          return;
        }
        const s = r?.getText();
        if (s) props.onDecoded(s);
        else {
          Log.error("Could not .getText of decoded QR code", {
            result: s,
          });
        }
      },
    });
  }

  function setupCameraEffect() {
    const p = setupCamera();
    p.then((r) => setHasCameraAccess(r.hasCameraAccess));
    return () => void p.then(cameraManagerRef.current.cleanup);
  }

  useEffect(() => {
    function listener(ev: any) {
      Log.debug("[Cam][visibilitychange] setting up", {
        event: ev,
        visibilityState: document.visibilityState,
      });
      if (document.visibilityState === "visible") {
        return setupCameraEffect();
      } else if (document.visibilityState === "hidden") {
        cameraManagerRef.current.cleanup();
      }
    }
    document.addEventListener("visibilitychange", listener);
    return () => {
      document.removeEventListener("visibilitychange", listener);
    };
  }, []);

  useIonViewDidEnter(() => {
    Log.debug("[Cam][useIonViewDidEnter] setting up");
    return setupCameraEffect();
  });

  useIonViewDidLeave(() => {
    Log.debug("[Cam][useIonViewDidLeave] cleaning up");
    cameraManagerRef.current.cleanup();
  });

  return (
    <div {...classNames(css.Cam, props.className)}>
      <video className={css.Video} ref={videoRef} autoPlay playsInline muted />
      {!hasCameraAccess && (
        <Card className={css.ScanNextButton} variant="green">
          <Card.Content className={css.Content}>
            <IconQrcode />
            Mangler adgang til kameraet
          </Card.Content>
        </Card>
      )}
    </div>
  );
}
